import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'
import { FormControl } from '@mui/material'
import MyInput from './MyInput'
import MyButton from './buttons'
import { FormError } from './form-error'
import { FormSuccess } from './form-success'
import { useTranslation } from 'react-i18next'
import { FaLock, FaUnlockAlt } from 'react-icons/fa'
import AuthService from '../../services/login'

const ResetPasswordPage = () => {
  const { token } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [password, setPassword] = useState('')
  const [passwordConfirmation, setPasswordConfirmation] = useState('')
  const [showPassword] = useState(false)
  const [showPasswordConfirmation] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [invalidFields, setInvalidFields] = useState(new Set())
  const [, setIsSubmitting] = useState(false)

  const schema = Yup.object().shape({
    password: Yup.string()
      .min(8, t('validation-password-min'))
      .matches(/[A-Z]/, t('validation-password-uppercase'))
      .matches(/[0-9]/, t('validation-password-number'))
      .required(t('validation-password-required')),
    passwordConfirmation: Yup.string()
      .oneOf([Yup.ref('password')], t('passwords-do-not-match'))
      .required(t('validation-password-confirmation-required')),
  })

  const handleSubmit = async (e: { preventDefault: () => void }) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSuccessMessage('')
    setErrorMessage('')
    setInvalidFields(new Set())

    try {
      await schema.validate(
        {
          password,
          passwordConfirmation,
        },
        { abortEarly: false },
      )

      let result
      if (token) {
        result = await AuthService.resetPassword(token, password)
        if (result.success) {
          setSuccessMessage(result.success)
          setTimeout(() => {
            navigate('/login')
          }, 3000)
        } else {
          setErrorMessage(result?.error || 'Une erreur est survenue')
        }
      } else {
        setErrorMessage(t('invalid-token'))
      }

      if (result && result.success) {
        setSuccessMessage(result.success)
        setTimeout(() => {
          navigate('/login')
        }, 3000)
      } else {
        setErrorMessage(result?.error || 'Une erreur est survenue')
      }
    } catch (validationErrors) {
      if (validationErrors instanceof Yup.ValidationError) {
        const newInvalidFields = new Set()
        let firstError: string | null = null
        validationErrors.inner.forEach(error => {
          if (error.path) {
            newInvalidFields.add(error.path)
            if (!firstError) {
              firstError = error.message
            }
          }
        })
        setInvalidFields(newInvalidFields)
        setErrorMessage(firstError || t('form-validation-error'))
      } else {
        setErrorMessage(t('unexpected-error'))
        console.error('Unexpected error:', validationErrors)
      }
    }
  }

  return (
    <div className='flex items-center justify-center min-h-screen bg-gray-100'>
      <div className='bg-white rounded-lg shadow-2xl max-w-lg w-full'>
        <form onSubmit={handleSubmit} className='space-y-4 text-center'>
          <h3 className='text-xl rounded-t-lg bg-anthracite p-5 text-white font-medium mb-6'>
            {t('reset-your-password')}
          </h3>

          <div className='space-y-5 m-8'>
            <FormControl fullWidth>
              <MyInput
                label={t('password')}
                type={showPassword ? 'text' : 'password'}
                value={password}
                showClearButton={false}
                onChange={value => setPassword(value)}
                className='password-input'
                icon={<FaLock />}
                isInvalid={invalidFields.has('password')}
              />
            </FormControl>

            <FormControl fullWidth>
              <MyInput
                label={t('confirm-password')}
                showClearButton={false}
                type={showPasswordConfirmation ? 'text' : 'password'}
                value={passwordConfirmation}
                onChange={value => setPasswordConfirmation(value)}
                className='password-confirmation-input'
                icon={<FaUnlockAlt />}
                isInvalid={invalidFields.has('passwordConfirmation')}
              />
            </FormControl>

            {errorMessage && <FormError message={errorMessage} />}
            {successMessage && <FormSuccess message={successMessage} />}

            <div className='flex justify-center mt-6'>
              <MyButton
                type='submit'
                variant='redpink-anthracite'
                className='w-2/3 mb-8 bg-redpink text-white hover:bg-anthracite transition-all'
                onClick={() => AuthService.logout()}
              >
                {t('reset-password')}
              </MyButton>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ResetPasswordPage
